// Odvozené texty vozu, které skládá frontend. Čisté funkce sdílené seznamem
// i detailem, aby se stejná sémantika neopisovala na dvou místech.

import type { TFunction } from 'i18next';

const DATE_FORMAT = new Intl.DateTimeFormat('cs-CZ', {
  day: 'numeric',
  month: 'numeric',
  year: 'numeric',
});

/** Převede ISO datum z API na `Date`; nevalidní nebo prázdná hodnota → `null`. */
export function parseApiDate(value: string | null | undefined): Date | null {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Lokální půlnoc daného dne — porovnání „dnů" nesmí ovlivnit čas ani časová zóna. */
function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Počet celých dnů mezi dneškem a datem (kladné = v budoucnu, záporné = v minulosti). */
export function daysUntil(value: string | null | undefined, today = new Date()): number | null {
  const date = parseApiDate(value);
  if (!date) {
    return null;
  }
  const diffMs = startOfDay(date).getTime() - startOfDay(today).getTime();
  return Math.round(diffMs / 86_400_000);
}

/**
 * Lokalizovaná dostupnost vozu složená ze dvou zdrojových dat (API je nespojuje):
 * budoucí uvedení do provozu → `od {datum}` (s vyřazením `{od} – {do}`), jinak `do {datum}`
 * pro vyřazený vůz a `—` pro vůz bez vyřazení.
 */
export function formatAvailability(
  t: TFunction,
  commissioningDate: string | null | undefined,
  retirementDate: string | null | undefined,
  today = new Date()
): string {
  const from = parseApiDate(commissioningDate);
  const until = parseApiDate(retirementDate);
  const isFutureCommissioning = from !== null && startOfDay(from) > startOfDay(today);

  if (isFutureCommissioning) {
    return until
      ? t('availability.range', {
          from: DATE_FORMAT.format(from),
          until: DATE_FORMAT.format(until),
        })
      : t('availability.from', { from: DATE_FORMAT.format(from) });
  }

  return until
    ? t('availability.until', { until: DATE_FORMAT.format(until) })
    : t('availability.none');
}

/** Lokalizovaný popisek expirace: datum + kolik dnů zbývá (nebo o kolik je propadlá). */
export function formatExpiry(
  t: TFunction,
  value: string | null | undefined,
  today = new Date()
): string {
  const date = parseApiDate(value);
  if (!date) {
    return t('expiry.empty');
  }

  const days = daysUntil(value, today) ?? 0;
  if (days === 0) {
    return `${DATE_FORMAT.format(date)} (${t('expiry.today')})`;
  }

  const relative =
    days > 0
      ? t('expiry.inDays', { count: days })
      : t('expiry.overdueDays', { count: Math.abs(days) });
  return `${DATE_FORMAT.format(date)} (${relative})`;
}
